import type { QuotesFragment } from '@mnd-apps/public/wp-generated/types';
import { useMediaQuery } from '@mnd-frontend/hooks';
import CardQuotation from '@mnd-frontend/ui/public/components/CardQuotation';
import Slider, { create2DSlideArray } from '@mnd-frontend/ui/public/components/Slider';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import SectionHeader from '@mnd-frontend/ui/public/components/wrappers/SectionHeader';
import { media } from '@mnd-frontend/ui/publish/components/utils';
import { BREAKPOINTS, SPACING_6, SPACING_10 } from '@mnd-frontend/ui/shared/tokens';
import Image from 'next/image';
import { type ComponentProps, useMemo } from 'react';
import styled from 'styled-components';

const ModuleContainer = styled.aside`
  padding-top: ${SPACING_6};
  padding-bottom: ${SPACING_6};
  position: relative;

  &:not(:has(> img)) {
    background: #F7F7F9; // @todo: use token */
  }

  @media (min-width: ${BREAKPOINTS.MD}){
      padding-top: ${SPACING_10};
      padding-bottom: ${SPACING_10};
  }
`;

const BackgroundImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -2;
  display: block;
`;

const formatImageData = ({ bgImage }: QuotesFragment) =>
  ({
    src: bgImage?.node.mediaItemUrl ?? '',
    alt: '',
    width: bgImage?.node.mediaDetails?.width ?? undefined,
    height: bgImage?.node.mediaDetails?.height ?? undefined,
  }) satisfies ComponentProps<typeof Image>;

const QuotesModule = ({ moduleData }: { moduleData: QuotesFragment }) => {
  const isDeviceMiddle = useMediaQuery(media.greaterThan('tablet'));
  const isDeviceLarge = useMediaQuery(media.greaterThan('normal'));
  const disableSlider =
    useMediaQuery(media.greaterThan('normal')) && (moduleData.data ?? []).length < 4;

  const numOfSlides = useMemo(() => {
    const numOfItems = moduleData.data?.length ?? 1;

    if (isDeviceLarge) {
      return 2;
    }

    if (isDeviceMiddle) {
      return 3;
    }

    return numOfItems;
  }, [isDeviceMiddle, isDeviceLarge, moduleData.data]);

  const slides = create2DSlideArray(moduleData.data ?? [], numOfSlides);

  return (
    <ModuleContainer>
      {moduleData.bgImage?.node.mediaItemUrl && (
        <BackgroundImage {...formatImageData(moduleData)} />
      )}
      <ContentMaxWidth>
        <SectionHeader
          headline={moduleData.sectionHeadline}
          subHeadline={moduleData.sectionSubheadline}
          colorScheme={moduleData.bgImage?.node.mediaItemUrl ? 'light' : 'dark'}
        />
        <Slider itemsLength={slides.length} isDisabled={disableSlider}>
          {Array.isArray(slides) &&
            slides.map(
              slideRow =>
                Array.isArray(slideRow) &&
                slideRow.map((item, i) => (
                  <CardQuotation
                    key={i}
                    quote={item?.quote ?? ''}
                    organisation={item?.organisation ?? ''}
                    name={item?.name ?? ''}
                    jobTitle={item?.jobTitle}
                    profileImage={item?.image}
                  />
                )),
            )}
        </Slider>
      </ContentMaxWidth>
    </ModuleContainer>
  );
};
export default QuotesModule;
